/**
 * Mobile Drawer Component
 *
 * Slide-out navigation drawer for mobile.
 */

import { useEffect, useCallback } from 'react';
import { cn } from '@/shared/lib';
import { useUIStore } from '@/state/stores/ui.store';
import { DrawerContent } from './drawer-content';
import { DRAWER_IDS } from '../../types';

export const MobileDrawer = () => {
  const { isDrawerOpen, closeDrawer } = useUIStore();
  const isOpen = isDrawerOpen(DRAWER_IDS.MOBILE_NAV);

  const handleClose = useCallback(() => {
    closeDrawer(DRAWER_IDS.MOBILE_NAV);
  }, [closeDrawer]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/50 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer Panel - Slides from Right */}
      <div
        className={cn(
          'fixed top-0 right-0 bottom-0 z-50 w-80 max-w-[85vw] bg-white shadow-xl',
          'transform transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <DrawerContent onClose={handleClose} />
      </div>
    </>
  );
};

export default MobileDrawer;
